import Expired from "components/Expired";
import Loader from "components/Loader";
import ThankYou from "components/ThankYou";
import Unsubscribe from "components/Unsubscribe";
import useSessionProvider from "providers/SessionProvider";
import useThemeProvider from "providers/ThemeProvider";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import useFetch from "use-http";

/**
 * Renders a survey's Thankyou / Unsubscribe / Expire template for a designer previewing it from
 * the portal.
 *
 * This screen has no logged-in user of its own. It used to call the authenticated
 * Designer/GetSurvey and rely on the request interceptor finding the portal's OIDC token in
 * localStorage, which only works when the portal and this app share an origin - they do on GCP
 * and do not on AWS, where the preview always 401'd. The portal now mints a short-lived
 * survey-scoped token and puts it in the link, and this screen passes it to the anonymous
 * Preview/GetSurvey endpoint instead.
 */
const Preview = () => {
    const { get, loading } = useFetch(process.env.REACT_APP_API_URL);
    const location = useLocation();
    const [type, setType] = useState('');
    const [failed, setFailed] = useState(false);
    const { setSessionId, setSurvey, setLogoPath, setStatus } = useSessionProvider();
    const { setTheme } = useThemeProvider();

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const survey_id = query.get('survey_id');
        const type_ = query.get('type');
        const token = query.get('token');
        setType(type_);
        setSessionId(-1);

        if (!token) {
            // An old link, or one that lost its token somewhere. There is nothing to fall back
            // to - the endpoint will not answer without one - so say so instead of rendering an
            // empty template.
            setFailed(true);
            setSurvey(null);
            return;
        }

        setFailed(false);
        get(`/Preview/GetSurvey?surveyId=${survey_id}&token=${encodeURIComponent(token)}`)
            .then((x) => {
                if (x && x.status && x.status === 200) {
                    setSurvey(x?.survey?.survey);
                    if (x?.survey?.survey?.customTheme && x?.survey?.survey?.theme) {
                        setTheme(x?.survey?.survey?.theme);
                    } else {
                        setTheme('');
                    }
                    if (type_ === 'Thankyou') {
                        setLogoPath(x?.survey?.survey?.thankyouPage.logo !== null ? x?.survey?.survey?.thankyouPage.logo : x?.survey?.survey?.logo);
                        setStatus('Completed')
                    }

                    if (type_ === 'Unsubscribe') {
                        setLogoPath(x?.survey?.survey?.unsubscribePage.logo !== null ? x?.survey?.survey?.unsubscribePage.logo : x?.survey?.survey?.logo);
                        setStatus('Unsubscribed')
                    }

                    if (type_ === 'Expire') {
                        setLogoPath(x?.survey?.survey?.expirePage?.logo !== null ? x?.survey?.survey?.expirePage?.logo : x?.survey?.survey?.logo);
                        setStatus('Expired')
                    }

                } else {
                    setFailed(true);
                    setSurvey(null);
                }
            })
            .catch((err) => {
                console.log({ err });
                setFailed(true);
                setSurvey(null);
            });
    }, [get, location.pathname, location.search, setLogoPath, setSessionId, setStatus, setSurvey, setTheme]);
    return (
        <div>
            {loading && <Loader />}
            {failed && !loading && (
                <div className="container container-md">
                    <p>This preview link is invalid or has expired. Please reopen the preview from the designer.</p>
                </div>
            )}
            {!failed && type === 'Thankyou' && <ThankYou />}
            {!failed && type === 'Unsubscribe' && <Unsubscribe />}
            {!failed && type === 'Expire' && <Expired />}
        </div>
    )
}

export default Preview;
